import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Button, Tooltip } from 'react-bootstrap';
import { Alarm } from 'react-bootstrap-icons';
import GetTargetDate from '../../utils/GetTargetDate';
import './DeadlineTooltip.css';

// "3d 14h", "6h 12m", "4m 30s" -- drops to the next-smaller unit once the
// bigger one hits zero, so the countdown stays informative all the way down
// to the final seconds instead of freezing at "0d".
function formatCountdown(diffMs) {
    const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
}

function DeadlineTooltip({
    weekNum,
    year,
    weekDeadlines
}) {
    const monthNames = [
                        'January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    let deadline = 'normal';
    let dow = 'Sunday';

    // For schedule-driven years (2026+), deadlinesArr holds real ISO deadline
    // timestamps per game rather than a hand-typed type string -- the latest
    // one in the week is the shared Sunday-noon-CT cutoff (see EnterPicks.js/
    // CurrentWeek.js for the same pattern). GetTargetDate has no branch for
    // those years, so calling it here doesn't mean "legacy year," it means
    // "the real deadline hasn't loaded yet" -- and its fallback silently
    // returns a *2025* date (the season before this one), which reads as a
    // real answer instead of a loading state. Never call it for 2026+; show
    // a loading placeholder for the brief window before deadlinesArr arrives
    // instead of a wrong-but-plausible-looking date.
    const isoDeadlines = (weekDeadlines || []).filter((d) => typeof d === 'string' && d.includes('T'));
    const isDynamicScheduleYear = Number(year) >= 2026;
    const stillLoading = isDynamicScheduleYear && isoDeadlines.length === 0;
    let tar = isoDeadlines.length
        ? new Date(Math.max(...isoDeadlines.map((d) => new Date(d).getTime())))
        : (isDynamicScheduleYear ? null : GetTargetDate(weekNum, deadline, year));

    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    const diffMs = tar ? tar.getTime() - now.getTime() : null;
    const isPast = diffMs !== null && diffMs <= 0;
    const isUrgent = !stillLoading && !isPast && diffMs < 3 * 60 * 60 * 1000; // under 3 hours

    return (
        <>
            <div className='deadline-tooltip-section'>
                <OverlayTrigger
                    placement='bottom'
                    overlay={
                        <Tooltip id='button-tooltip-2'>
                            {
                                stillLoading
                                    ? 'Loading this week\'s deadline...'
                                    : <>The deadline to submit a pick is {monthNames[tar.getMonth()]} {tar.getDate()} at 12pm CST.
                                    If a game is being played before {dow} at 12, the pick must be submitted before the game's kickoff.</>
                            }
                        </Tooltip>}
                    >
                    {({ ref, ...triggerHandler }) => (
                        <Button
                        variant='light'
                        {...triggerHandler}
                        className={'d-inline-flex align-items-center tooltip-outline' + (isUrgent ? ' tooltip-outline-urgent' : '')}
                        >
                        <Alarm className='deadline-tooltip-icon' />
                        <span className='deadline-tooltip-display' ref={ref}>
                            {
                                stillLoading
                                    ? 'Loading deadline...'
                                    : <>
                                        Weekly Deadline - {monthNames[tar.getMonth()]} {tar.getDate()}
                                        <span className='deadline-tooltip-countdown'>
                                            {isPast ? 'Locked' : formatCountdown(diffMs) + ' left'}
                                        </span>
                                    </>
                            }
                        </span>
                        </Button>
                    )}
                </OverlayTrigger>
            </div>
        </>
    );

}

export default DeadlineTooltip;
